
import { useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  DatePicker,
  Field,
  Input,
  PhoneInput,
  Textarea,
  TimeInput,
  Select,
} from '@/shared/ui';
import {
  fieldFormatError,
  isChoiceField,
  isDirectoryField,
  isEmptyValue,
  isFullWidthField,
  isGroupField,
  isGroupRowFilled,
  isLayoutField,
  groupRows,
  resolveForm,
  type FormField,
  type FormFieldOption,
  type FormSchema,
  type FormValues,
} from '@/shared/api/forms';
import { DirectoryField } from './DirectoryField';
import { FolderField } from './FolderField';
import { FormNote } from './FormNote';
import { GroupField } from './GroupField';
import { UserField } from './UserField';
import { FileField } from './FileField';
import { cn } from '@/shared/lib/cn';
import styles from './DynamicFormFields.module.css';

export interface DynamicFormFieldsProps {
  schema: FormSchema;
  values: FormValues;
  onChange: (values: FormValues) => void;
  disabled?: boolean;
  showErrors?: boolean;
  progressive?: boolean;
}

const INPUT_TYPE: Partial<Record<FormField['type'], string>> = {
  email: 'email',
  phone: 'tel',
  url: 'url',
};

const AUTOCOMPLETE: Partial<Record<FormField['type'], string>> = {
  email: 'email',
  url: 'url',
};

function cleanTyped(type: FormField['type'], raw: string): string {
  return type === 'email' || type === 'url' ? raw.replace(/\s+/g, '') : raw;
}

function withScheme(raw: string): string {
  const value = raw.trim();
  if (value === '' || /^[a-z][\w+.-]*:/i.test(value)) return value;
  return `https://${value}`;
}

export const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
  schema,
  values,
  onChange,
  disabled,
  showErrors = false,
  progressive = false,
}) => {
  const resolved = useMemo(() => resolveForm(schema, values), [schema, values]);
  const visible = useMemo(() => new Set(resolved.visibleKeys), [resolved]);

  const lockedFrom = useMemo(() => {
    if (!progressive || showErrors) return Infinity;
    let missing = false;
    for (let i = 0; i < schema.fields.length; i++) {
      const f = schema.fields[i];
      if (f.type === 'section') {
        if (missing) return i;
        continue;
      }
      if (!f.required || !visible.has(f.key)) continue;
      const empty = isGroupField(f)
        ? !groupRows(values[f.key]).some((row) => isGroupRowFilled(f.columns ?? [], row))
        : isEmptyValue(values[f.key]);
      if (empty) missing = true;
    }
    return Infinity;
  }, [progressive, showErrors, schema, visible, values]);
  const upcoming = schema.fields
    .slice(lockedFrom)
    .filter((f) => f.type === 'section' && visible.has(f.key))
    .map((f) => f.label);
  const [touched, setTouched] = useState<Record<string, true>>({});
  const markTouched = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const errorOf = (field: FormField) =>
    (showErrors || touched[field.key] === true
      ? fieldFormatError(field, values[field.key])
      : null) ?? undefined;

  const latestValues = useRef(values);
  latestValues.current = values;

  const setValue = (field: FormField, value: unknown) => {
    const next: FormValues = { ...latestValues.current, [field.key]: value };
    latestValues.current = next;
    const nextResolved = resolveForm(schema, next);
    const nextVisible = new Set(nextResolved.visibleKeys);
    for (const f of schema.fields) {
      if (isDirectoryField(f)) {
        if (f.source?.parentFromField === field.key && f.key !== field.key) delete next[f.key];
        continue;
      }
      if (!isChoiceField(f) || !nextVisible.has(f.key) || next[f.key] == null) continue;
      const allowed = nextResolved.values[f.key];
      if (allowed == null) delete next[f.key];
      else next[f.key] = allowed;
    }
    onChange(next);
  };

  return (
    <div className={styles.fields}>
      {schema.fields
        .filter((f, i) => i < lockedFrom && visible.has(f.key))
        .map((field) =>
          field.type === 'note' ? (
            <div key={field.key} className={styles.wide}>
              <FormNote title={field.label} content={field.content ?? ''} />
            </div>
          ) : isGroupField(field) ? (
            <div key={field.key} className={cn(styles.field, styles.wide)}>
              <GroupField
                field={field}
                value={values[field.key]}
                disabled={disabled}
                onChange={(rows) => setValue(field, rows)}
              />
            </div>
          ) : isLayoutField(field) ? (
            <div key={field.key} className={cn(styles.section, styles.wide)}>
              <span className={styles.sectionTitle}>{field.label}</span>
              {field.hint && <p className={styles.hint}>{field.hint}</p>}
            </div>
          ) : (
            <div key={field.key} className={cn(styles.field, isFullWidthField(field) && styles.wide)}>
              { }
              {field.type === 'folder' ? (
                <FolderField
                  field={field}
                  value={values[field.key]}
                  disabled={disabled}
                  error={errorOf(field)}
                  onChange={(next) => setValue(field, next)}
                />
              ) : field.type === 'checkbox' ? (
                <FieldControl
                  field={field}
                  value={values[field.key]}
                  options={resolved.optionsByKey[field.key]}
                  disabled={disabled}
                  onChange={setValue}
                />
              ) : (
                <Field
                  label={field.label}
                  required={field.required}
                  hint={field.hint}
                  hintAs={field.hintAs}
                  error={errorOf(field)}
                  floated={
                    field.type === 'select' ||
                    field.type === 'multiselect' ||
                    field.type === 'directory'
                  }
                >
                  <FieldControl
                    id={`ff-${field.key}`}
                    placeholder={field.placeholder}
                    field={field}
                    value={values[field.key]}
                    options={resolved.optionsByKey[field.key]}
                    parentValue={
                      field.source?.parentFromField
                        ? values[field.source.parentFromField]
                        : undefined
                    }
                    disabled={disabled}
                    invalid={errorOf(field) != null}
                    onBlur={() => markTouched(field.key)}
                    onChange={setValue}
                  />
                </Field>
              )}
              {field.type === 'checkbox' && field.hint && (
                <p className={styles.hint}>{field.hint}</p>
              )}
            </div>
          ),
        )}
      {upcoming.length > 0 && (
        <p className={cn(styles.upcoming, styles.wide)}>
          Дальше — {upcoming.join(', ')}. {upcoming.length > 1 ? 'Откроются' : 'Откроется'}, когда
          заполните обязательные поля выше.
        </p>
      )}
    </div>
  );
};

const FieldControl: React.FC<{
  field: FormField;
  value: unknown;
  id?: string;
  placeholder?: string;
  options?: FormFieldOption[];
  parentValue?: unknown;
  disabled?: boolean;
  'data-in-field'?: string;
  invalid?: boolean;
  onBlur?: () => void;
  onChange: (field: FormField, value: unknown) => void;
}> = ({
  field,
  value,
  id,
  placeholder,
  options,
  parentValue,
  disabled,
  invalid,
  onBlur,
  onChange,
  ...rest
}) => {
  const inField = { 'data-in-field': rest['data-in-field'] };
  switch (field.type) {
    case 'directory':
      return (
        <DirectoryField
          id={id}
          field={field}
          value={value}
          parentValue={parentValue}
          disabled={disabled}
          onChange={(next) => onChange(field, next)}
          {...inField}
        />
      );
    case 'phone':
      return (
        <PhoneInput
          id={id}
          value={(value as string) ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          invalid={invalid}
          maxLength={20}
          onBlur={onBlur}
          onChange={(next) => onChange(field, next)}
        />
      );
    case 'text':
    case 'email':
    case 'url':
      return (
        <Input
          id={id}
          type={INPUT_TYPE[field.type]}
          value={(value as string) ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          invalid={invalid}
          maxLength={500}
          autoComplete={AUTOCOMPLETE[field.type]}
          onBlur={(e) => {
            if (field.type === 'url') {
              const done = withScheme(e.target.value);
              if (done !== value) onChange(field, done);
            }
            onBlur?.();
          }}
          onChange={(e) => onChange(field, cleanTyped(field.type, e.target.value))}
        />
      );
    case 'textarea':
      return (
        <Textarea
          id={id}
          className={styles.textarea}
          value={(value as string) ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={5000}
          onChange={(e) => onChange(field, e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value == null ? '' : String(value)}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) =>
            onChange(field, e.target.value === '' ? undefined : Number(e.target.value))
          }
        />
      );
    case 'file':
      return (
        <FileField
          id={id}
          value={value}
          maxFiles={field.maxFiles}
          disabled={disabled}
          onChange={(next) => onChange(field, next)}
        />
      );
    case 'user':
      return (
        <UserField
          id={id}
          value={value}
          multiple={field.display?.multiple === true}
          allowOther={field.allowOther === true}
          personnelNumber={field.personnelNumber}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(next) => onChange(field, next)}
        />
      );
    case 'time':
      return (
        <TimeInput
          id={id}
          value={(value as string) ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          onChange={(next) => onChange(field, next || undefined)}
        />
      );
    case 'date':
      return (
        <DatePicker
          id={id}
          value={(value as string) ?? ''}
          disabled={disabled}
          onChange={(next) => onChange(field, next || undefined)}
        />
      );
    case 'checkbox':
      return (
        <Checkbox
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(field, checked === true)}
          label={
            <>
              {field.label}
              {field.required && <span aria-hidden> *</span>}
            </>
          }
        />
      );
    case 'select': {
      const list = options ?? [];
      const parentEmpty = Boolean(field.dependsOn) && list.length === 0;
      return (
        <Select
          id={id}
          aria-label={field.label}
          {...inField}
          value={(value as string) ?? ''}
          disabled={disabled || parentEmpty}
          onValueChange={(v) => onChange(field, v || undefined)}
          options={[
            {
              value: '',
              label: parentEmpty ? 'Сначала выберите поле выше' : field.placeholder || 'Выберите…',
            },
            ...list,
          ]}
        />
      );
    }
    case 'multiselect': {
      const list = options ?? [];
      const selected = (value as string[]) ?? [];
      return (
        <div className={styles.multiselect} role="group" aria-label={field.label}>
          {list.length === 0 && (
            <span className={styles.hint}>
              {field.dependsOn ? 'Сначала выберите поле выше' : 'Вариантов нет'}
            </span>
          )}
          {list.map((o) => (
            <Checkbox
              key={o.value}
              checked={selected.includes(o.value)}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange(
                  field,
                  checked === true
                    ? [...selected, o.value]
                    : selected.filter((v) => v !== o.value),
                )
              }
              label={o.label}
            />
          ))}
        </div>
      );
    }
    case 'section':
    case 'note':
    case 'group':
      return null;
  }
};
